/**
 * GAME RESULTS — the one place a client sees every play from every game.
 *
 * The page asks the server for ONE kind of row, whichever game filled it, and
 * draws only the columns and filters that game can really fill. It never shows
 * a filter that cannot work for this client's data, and it never opens a
 * survey's answers: a clinic gift shows the gift, the voucher and its status.
 *
 * The look is Review Shield Results'. That page is not changed by this one.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Gamepad2, Loader2, Lock, Settings, RotateCcw, Users, Ticket, Trophy,
    MousePointerClick, Clock, Home, Store,
} from 'lucide-react';
import api from '../lib/api';
import { LockedTables } from '../components/game_results/LockedTables';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Sheet, SheetContent } from '../components/ui/sheet';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { parseUtc } from '../lib/datetime';
import { format } from 'date-fns';
import { GameDonuts, buildGameDonuts } from '../components/shared/GameDonuts';
import {
    GameFilterBar, useGameFilters, rowMatches, filterLabel,
} from '../components/shared/GameFilterBar';
import { PlayerStatusPill, StarPickCell, starPickCounts } from '../components/shared/GamePlayerChips';
import { PlayerHistoryPanel } from '../components/shared/PlayerHistoryPanel';

// The row the page can draw, in table order. The server says which of them
// this game fills; anything else is simply not drawn.
const COLUMN_ORDER = [
    'at', 'player', 'email', 'where', 'sponsor', 'table_number', 'winner_pick', 'fight_picks', 'star_pick',
    'goal', 'result', 'prize', 'voucher', 'voucher_status',
    'valid_from', 'expires_at', 'redeemed_at', 'redeemed_by',
];

// Columns that only fill in once the player actually scratches.
const AFTER_SCRATCH = ['goal', 'result', 'prize', 'voucher', 'voucher_status',
    'expires_at', 'redeemed_at', 'redeemed_by'];

// A convenience only — the server decides what this user may actually see.
const ORG_MEMORY = 'yot_game_org';

const stamp = (value) => {
    if (!value) return '—';
    const d = parseUtc(value);
    return d ? format(d, 'MMM d, HH:mm:ss') : '—';
};

const SummaryCard = ({ icon: Icon, label, value, testId }) => (
    <Card data-testid={testId}>
        <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Icon className="w-4 h-4" />
                <span className="text-xs">{label}</span>
            </div>
            <p className="text-2xl font-bold">{value}</p>
        </CardContent>
    </Card>
);

const StateBadge = ({ state, labels }) => {
    if (!state || state === 'none') return <span className="text-muted-foreground text-xs">—</span>;
    const tone = state === 'redeemed' ? 'default'
        : state === 'expired' ? 'destructive' : 'secondary';
    return <Badge variant={tone} className="text-xs">{labels[state] || state}</Badge>;
};

/** No tick, no page. A direct link lands here instead of a blank screen; the
 *  tick itself is in the permissions screen's "Goal Game" group. */
const NoGoalAccess = () => {
    const { t } = useTranslation();
    return (
        <div className="p-8 text-center" data-testid="goal-no-access">
            <Lock className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-lg font-medium">{t('goalAccess.title')}</p>
            <p className="text-sm text-muted-foreground">{t('goalAccess.body')}</p>
        </div>
    );
};

function GameResultsPage({ managerToken = null, onSessionEnded = null }) {
    // NEO: the same page, read through a manager session instead of a portal
    // login. The server decides the scope from that signed token.
    const neo = managerToken
        ? { headers: { 'X-Manager-Session': managerToken } } : {};
    const { t } = useTranslation();
    const { isSuperAdmin, hasPermission } = useAuth();
    const [params, setParams] = useSearchParams();
    const { active, toggle, clear, count: filterCount } = useGameFilters(params, setParams);
    const [org, setOrg] = useState(
        params.get('organization_id') || localStorage.getItem(ORG_MEMORY) || '');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [game, setGame] = useState(params.get('game') || '');
    const [result, setResult] = useState('all');
    const [where, setWhere] = useState('all');
    const [voucherStatus, setVoucherStatus] = useState('all');
    const [sponsor, setSponsor] = useState('all');
    const [search, setSearch] = useState('');
    const [openRow, setOpenRow] = useState(null);
    const [openPlayer, setOpenPlayer] = useState(null);
    const [customize, setCustomize] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/game-results', {
                ...neo,
                params: {
                    organization_id: org || undefined,
                    game: game || undefined,
                    result: result !== 'all' ? result : undefined,
                    where: where !== 'all' ? where : undefined,
                    voucher_status: voucherStatus !== 'all' ? voucherStatus : undefined,
                    sponsor: sponsor !== 'all' ? sponsor : undefined,
                    search: search.trim() || undefined,
                },
            });
            setData(res.data);
            if (!game && res.data.game) setGame(res.data.game);
            if (res.data.organization_id && res.data.organization_id !== org) {
                setOrg(res.data.organization_id);
            }
        } catch (e) {
            if (e.response?.status === 401 && onSessionEnded) onSessionEnded();
            else throw e;
        } finally {
            setLoading(false);
        }
    }, [org, game, result, where, voucherStatus, sponsor, search, managerToken]);

    useEffect(() => {
        const handle = setTimeout(load, 250);
        return () => clearTimeout(handle);
    }, [load]);

    const caps = data?.capabilities || { columns: [], filters: [] };
    const hiddenColumns = data?.prefs?.hidden_columns || [];
    const hiddenFilters = data?.prefs?.hidden_filters || [];
    const columns = useMemo(
        () => COLUMN_ORDER.filter((c) => (caps.columns || []).includes(c) && !hiddenColumns.includes(c)),
        [caps.columns, hiddenColumns],
    );
    const showFilter = (name) => (caps.filters || []).includes(name) && !hiddenFilters.includes(name);

    const stateLabels = {
        unused: t('gameResults.state.unused', 'Not used yet'),
        redeemed: t('gameResults.state.redeemed', 'Used'),
        expired: t('gameResults.state.expired', 'Ran out'),
    };

    const saveHidden = async (kind, key, on) => {
        const current = kind === 'column' ? hiddenColumns : hiddenFilters;
        const next = on ? current.filter((k) => k !== key) : [...current, key];
        await api.put('/game-results/prefs', {
            organization_id: data.organization_id,
            ...(kind === 'column' ? { hidden_columns: next } : { hidden_filters: next }),
        });
        load();
    };

    const resetHidden = async () => {
        await api.put('/game-results/prefs', {
            organization_id: data.organization_id,
            hidden_columns: [], hidden_filters: [],
        });
        load();
    };

    const header = (key) => t(`gameResults.col.${key}`, key);

    const cell = (row, key) => {
        // Nobody has scratched yet: say so in plain words instead of a dash.
        if (row.scratch_count === 0 && AFTER_SCRATCH.includes(key)) {
            return <span className="text-xs text-orange-600 dark:text-orange-400"
                         data-testid={`game-results-waiting-${key}`}>
                {t('gameResults.waiting', 'Waiting')}
            </span>;
        }
        switch (key) {
            // The table staff named, with the audit line under it: who set it
            // and when. Both come from the log the staff screen writes.
            case 'table_number':
                return row.table_number ? (
                    <span className="block" data-testid={`game-results-table-${row.row_id}`}>
                        <span className="text-sm font-semibold">{row.table_number}</span>
                        {row.table_number_note && (
                            <span className="block text-[11px] text-muted-foreground">
                                {row.table_number_note}
                            </span>
                        )}
                    </span>
                ) : <span className="text-muted-foreground text-xs">—</span>;
            case 'star_pick':
                return <StarPickCell name={row.star_pick}
                                     count={starCounts[(row.star_pick || '').trim()]} t={t}
                                     testId={`game-results-star-${row.row_id}`} />;
            case 'at':
            case 'goal':
            case 'valid_from':
            case 'expires_at':
            case 'redeemed_at':
                return <span className="whitespace-nowrap text-sm">{stamp(row[key])}</span>;
            case 'email':
                return row.email ? (
                    <button type="button" onClick={(e) => { e.stopPropagation(); (row.status ? setOpenPlayer(row) : setOpenRow(row)); }}
                            className="text-emerald-600 dark:text-emerald-400 hover:underline text-sm"
                            data-testid={`game-results-email-${row.row_id}`}>
                        {row.email}
                    </button>
                ) : <span className="text-muted-foreground text-xs">—</span>;
            case 'voucher':
                return row.voucher ? (
                    <a href={`/voucher/${row.voucher}`} target="_blank" rel="noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-mono"
                       data-testid={`game-results-voucher-${row.row_id}`}>
                        {row.voucher}
                    </a>
                ) : <span className="text-muted-foreground text-xs">—</span>;
            case 'voucher_status':
                return <StateBadge state={row.voucher_status} labels={stateLabels} />;
            case 'result':
                return (
                    <Badge variant={row.result === 'lose' ? 'secondary' : 'default'} className="text-xs">
                        {t(`gameResults.result.${row.result}`, row.result || '—')}
                    </Badge>
                );
            case 'where':
                return row.where ? (
                    <span className="inline-flex items-center gap-1 text-sm">
                        {row.where === 'at_home' ? <Home className="w-3.5 h-3.5" /> : <Store className="w-3.5 h-3.5" />}
                        {t(`gameResults.where.${row.where}`, row.where)}
                    </span>
                ) : <span className="text-muted-foreground text-xs">—</span>;
            default:
                return <span className="text-sm">{row[key] || <span className="text-muted-foreground text-xs">—</span>}</span>;
        }
    };

    if (loading && !data) {
        return <div className="p-8" data-testid="game-results-loading"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
    }

    const summary = data?.summary || {};
    const allRows = data?.rows || [];
    // The donuts count every row the server sent, so a slice never vanishes
    // while it is the one being filtered on.
    const donuts = buildGameDonuts(allRows, t, { status: !!caps.player_rows });
    const rows = allRows.filter((r) => rowMatches(r, active));
    // Counted over every row of this game night, filters included or not.
    const starCounts = starPickCounts(allRows);
    const sponsorNames = [...new Set((data?.sponsor_summary || []).map((r) => r.sponsor_name)
        .concat(allRows.map((r) => r.sponsor)).filter(Boolean))].sort();
    // With a slice chosen, every count on the page follows it too.
    const shown = filterCount === 0 ? summary : {
        players: new Set(rows.map((r) => r.player_key || r.row_id)).size,
        scratches: rows.reduce((n, r) => n + (Number(r.scratch_count) || 0), 0),
        wins: rows.reduce((n, r) => n + (Number(r.win_count) || 0), 0),
        vouchers_issued: rows.filter((r) => r.voucher).length,
        vouchers_redeemed: rows.filter((r) => r.voucher_status === 'redeemed').length,
        vouchers_unused: rows.filter((r) => r.voucher_status === 'unused').length,
        vouchers_expired: rows.filter((r) => r.voucher_status === 'expired').length,
        on_site: rows.filter((r) => r.where === 'on_site').length,
        at_home: rows.filter((r) => r.where === 'at_home').length,
    };
    if (filterCount > 0) {
        shown.misses = summary.misses === null || summary.misses === undefined
            ? summary.misses : Math.max(0, shown.scratches - shown.wins);
        shown.win_percent = shown.scratches
            ? Math.round((1000 * shown.wins) / shown.scratches) / 10 : 0;
    }

    return (
        <div className="p-4 sm:p-6 space-y-5 max-w-[1500px] mx-auto" data-testid="game-results-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="game-results-org">
                        <Gamepad2 className="w-6 h-6 text-blue-600" />
                        {t('gameResults.title', 'Game Results')}
                        {data?.organization_name ? ` — ${data.organization_name}` : ''}
                    </h1>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    {(data?.organizations || []).length > 1 && (
                        <Select value={org} onValueChange={(v) => {
                            localStorage.setItem(ORG_MEMORY, v);
                            setGame('');
                            setOrg(v);
                        }}>
                            <SelectTrigger className="w-full sm:w-64 min-h-[48px]" data-testid="game-results-org-picker">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {(data?.organizations || []).map((o) => (
                                    <SelectItem key={o.id} value={o.id} data-testid={`game-results-org-${o.id}`}>
                                        {o.name}{o.demo ? ` · ${t('gameResults.demoTag', 'Demo')}` : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    {(data?.games || []).length > 1 && (
                        <Select value={game} onValueChange={(v) => setGame(v)}>
                            <SelectTrigger className="w-full sm:w-56" data-testid="game-results-game-picker">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {(data?.games || []).map((g) => (
                                    <SelectItem key={g.id} value={g.id} data-testid={`game-results-game-${g.id}`}>
                                        {t(g.label_key, g.id)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    {isSuperAdmin() && data && (
                        <Button variant="outline" size="icon" onClick={() => setCustomize(true)}
                                data-testid="game-results-customize-btn"
                                title={t('gameResults.customize', 'Customize')}>
                            <Settings className="w-4 h-4" />
                        </Button>
                    )}
                </div>
            </div>

            {(data?.games || []).length === 0 ? (
                <Card><CardContent className="py-12 text-center">
                    <Gamepad2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-lg font-medium">{t('gameResults.emptyTitle', 'No games yet')}</p>
                    <p className="text-sm text-muted-foreground">
                        {t('gameResults.emptyBody', 'Results show up here as soon as people start playing.')}
                    </p>
                </CardContent></Card>
            ) : (
                <>
                    {/* The first screen answers "how did my game do?" with no filtering. */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="game-results-summary">
                        <SummaryCard icon={Users} label={t('gameResults.sum.players', 'Players')} value={shown.players ?? 0} testId="game-results-sum-players" />
                        <SummaryCard icon={MousePointerClick} label={t('gameResults.sum.scratches', 'Scratches')} value={shown.scratches ?? 0} testId="game-results-sum-scratches" />
                        <SummaryCard icon={Trophy} label={t('gameResults.sum.wins', 'Wins')} value={shown.wins ?? 0} testId="game-results-sum-wins" />
                        {shown.misses !== null && shown.misses !== undefined && (
                            <SummaryCard icon={Clock} label={t('gameResults.sum.misses', 'Missed')} value={shown.misses} testId="game-results-sum-misses" />
                        )}
                        <SummaryCard icon={Trophy} label={t('gameResults.sum.winPercent', 'Win %')} value={`${shown.win_percent ?? 0}%`} testId="game-results-sum-winpct" />
                        <SummaryCard icon={Ticket} label={t('gameResults.sum.issued', 'Vouchers given')} value={shown.vouchers_issued ?? 0} testId="game-results-sum-issued" />
                        <SummaryCard icon={Ticket} label={t('gameResults.sum.redeemed', 'Vouchers used')} value={shown.vouchers_redeemed ?? 0} testId="game-results-sum-redeemed" />
                        <SummaryCard icon={Ticket} label={t('gameResults.sum.unused', 'Not used yet')} value={shown.vouchers_unused ?? 0} testId="game-results-sum-unused" />
                        <SummaryCard icon={Ticket} label={t('gameResults.sum.expired', 'Ran out')} value={shown.vouchers_expired ?? 0} testId="game-results-sum-expired" />
                        {showFilter('where') && (
                            <>
                                <SummaryCard icon={Store} label={t('gameResults.sum.onSite', 'Played here')} value={shown.on_site ?? 0} testId="game-results-sum-onsite" />
                                <SummaryCard icon={Home} label={t('gameResults.sum.atHome', 'Played at home')} value={shown.at_home ?? 0} testId="game-results-sum-athome" />
                            </>
                        )}
                    </div>

                    <GameDonuts donuts={donuts} active={active} onToggle={toggle} prefix="game-results" />

                    <GameFilterBar active={active} onToggle={toggle} onClear={clear}
                                   count={filterCount} t={t} prefix="game-results"
                                   labelFor={(id, key) => filterLabel(t, id, key)} />

                    {/* Filter bar — only the filters this game can really fill. */}
                    <div className="flex flex-wrap gap-3" data-testid="game-results-filters">
                        {showFilter('search') && (
                            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                                   placeholder={t('gameResults.searchPlaceholder', 'Search player, prize, voucher…')}
                                   className="w-full sm:w-80" data-testid="game-results-search" />
                        )}
                        {showFilter('result') && (
                            <Select value={result} onValueChange={setResult}>
                                <SelectTrigger className="w-44" data-testid="game-results-filter-result"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('gameResults.filter.allResults', 'All results')}</SelectItem>
                                    <SelectItem value="win">{t('gameResults.result.win', 'Won')}</SelectItem>
                                    <SelectItem value="lose">{t('gameResults.result.lose', 'Missed')}</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                        {showFilter('where') && (
                            <Select value={where} onValueChange={setWhere}>
                                <SelectTrigger className="w-44" data-testid="game-results-filter-where"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('gameResults.filter.allWhere', 'Anywhere')}</SelectItem>
                                    <SelectItem value="on_site">{t('gameResults.where.on_site', 'Played here')}</SelectItem>
                                    <SelectItem value="at_home">{t('gameResults.where.at_home', 'Played at home')}</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                        {/* AT-HOME SPONSORS — who paid for that player tonight. */}
                        {showFilter('sponsor') && sponsorNames.length > 0 && (
                            <Select value={sponsor} onValueChange={setSponsor}>
                                <SelectTrigger className="w-48" data-testid="game-results-filter-sponsor"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('goalGame.sponsorAll')}</SelectItem>
                                    {sponsorNames.map((name) => (
                                        <SelectItem key={name} value={name}>{name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        {showFilter('voucher_status') && (
                            <Select value={voucherStatus} onValueChange={setVoucherStatus}>
                                <SelectTrigger className="w-48" data-testid="game-results-filter-voucher"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('gameResults.filter.allVouchers', 'All vouchers')}</SelectItem>
                                    <SelectItem value="unused">{stateLabels.unused}</SelectItem>
                                    <SelectItem value="redeemed">{stateLabels.redeemed}</SelectItem>
                                    <SelectItem value="expired">{stateLabels.expired}</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    <Card>
                        <CardContent className="p-0 overflow-x-auto">
                            {rows.length === 0 ? (
                                <div className="text-center py-12 px-4" data-testid="game-results-empty-rows">
                                    <p className="text-lg font-medium">
                                        {filterCount > 0
                                            ? t('gameBoard.noneMatch', 'No players match these filters')
                                            : t('gameResults.noRowsTitle', 'Nothing to show yet')}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {t('gameResults.noRowsBody', 'Every play will appear here, newest first.')}
                                    </p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            {caps.player_rows && (
                                                <TableHead>{t('gameBoard.playerStatus', 'Status')}</TableHead>
                                            )}
                                            {columns.map((c) => <TableHead key={c}>{header(c)}</TableHead>)}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {rows.map((row) => (
                                            <TableRow key={row.row_id}
                                                      onClick={() => (row.status ? setOpenPlayer(row) : setOpenRow(row))}
                                                      className="cursor-pointer"
                                                      data-testid={`game-results-row-${row.row_id}`}>
                                                {caps.player_rows && (
                                                    <TableCell>
                                                        <PlayerStatusPill status={row.status} t={t}
                                                                          testId={`game-results-status-${row.row_id}`} />
                                                    </TableCell>
                                                )}
                                                {columns.map((c) => <TableCell key={c}>{cell(row, c)}</TableCell>)}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>

                    {/* The cards locked to one table, beside the table audit
                        lines owners already read here. Both need the table tick. */}
                    {game === 'goal_game' && (neo.headers || hasPermission('manage_table_numbers')) && (
                        <LockedTables organizationId={data?.organization_id} neo={neo} />
                    )}

                    {(data?.by_goal || []).length > 0 && (
                        <Card data-testid="game-results-by-goal">
                            <CardHeader><CardTitle className="text-base">{t('gameResults.byGoal', 'By goal')}</CardTitle></CardHeader>
                            <CardContent className="space-y-1">
                                {data.by_goal.map((g) => (
                                    <div key={g.goal} className="flex justify-between text-sm border-b last:border-0 py-1.5">
                                        <span>{stamp(g.goal)}</span>
                                        <span className="text-muted-foreground">
                                            {t('gameResults.goalLine', '{{plays}} played · {{wins}} won', { plays: g.plays, wins: g.wins })}
                                        </span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {(data?.by_prize || []).length > 0 && (
                        <Card data-testid="game-results-by-prize">
                            <CardHeader><CardTitle className="text-base">{t('gameResults.byPrize', 'By prize')}</CardTitle></CardHeader>
                            <CardContent className="space-y-1">
                                {data.by_prize.map((p) => (
                                    <div key={p.prize} className="flex justify-between text-sm border-b last:border-0 py-1.5">
                                        <span>{p.prize}</span>
                                        <span className="text-muted-foreground">
                                            {t('gameResults.prizeLine', '{{won}} won · {{redeemed}} used · {{left}} left',
                                                { won: p.won, redeemed: p.redeemed, left: p.left })}
                                        </span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {!!(data?.picks && Object.keys(data.picks).length) && (
                        <Card data-testid="game-results-picks">
                            <CardHeader><CardTitle className="text-base">{t('gameResults.picks', 'Winner pick and Star pick')}</CardTitle></CardHeader>
                            <CardContent className="space-y-1 text-sm">
                                {data.picks.winner_pick && (
                                    <div className="flex justify-between border-b py-1.5" data-testid="game-results-winner-pick">
                                        <span>{t('gameResults.winnerPick', 'Winner pick')}</span>
                                        <span className="text-muted-foreground">
                                            {t('gameResults.pickLine', '{{total}} picks · {{settled}} paid',
                                                { total: data.picks.winner_pick.total, settled: data.picks.winner_pick.settled })}
                                        </span>
                                    </div>
                                )}
                                {data.picks.star_pick && (
                                    <div className="flex justify-between py-1.5" data-testid="game-results-star-pick">
                                        <span>{t('gameResults.starPick', 'Star pick')}</span>
                                        <span className="text-muted-foreground">
                                            {t('gameResults.starLine', '{{total}} plays · {{doubled}} doubled',
                                                { total: data.picks.star_pick.total, doubled: data.picks.star_pick.doubled })}
                                        </span>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {(data?.gifts_later || []).length > 0 && (
                        <Card data-testid="game-results-gifts-later">
                            <CardHeader><CardTitle className="text-base">{t('gameResults.giftsLater', 'Gifts for later')}</CardTitle></CardHeader>
                            <CardContent className="space-y-1">
                                {data.gifts_later.map((row) => (
                                    <div key={row.row_id} className="flex flex-wrap justify-between gap-2 text-sm border-b last:border-0 py-1.5">
                                        <span>{row.prize}</span>
                                        <span className="text-muted-foreground">
                                            {t('gameResults.laterLine', 'Given {{given}} · good from {{from}} · ends {{until}}',
                                                { given: stamp(row.at), from: stamp(row.valid_from), until: stamp(row.expires_at) })}
                                        </span>
                                        <StateBadge state={row.voucher_status} labels={stateLabels} />
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {(data?.buttons || []).length > 0 && (
                        <Card data-testid="game-results-buttons">
                            <CardHeader><CardTitle className="text-base">{t('gameResults.buttons', 'Buttons')}</CardTitle></CardHeader>
                            <CardContent className="space-y-1">
                                {data.buttons.map((b) => (
                                    <div key={b.button} className="flex justify-between text-sm border-b last:border-0 py-1.5"
                                         data-testid={`game-results-button-${b.button}`}>
                                        <span>{t(`gameResults.button.${b.button}`, b.button)}</span>
                                        <span className="text-muted-foreground">
                                            {t('gameResults.buttonLine', '{{taps}} taps · {{devices}} phones · {{pct}}% of players',
                                                { taps: b.taps, devices: b.devices, pct: b.percent_of_players })}
                                        </span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* Tap a player to see their whole history — the same panel Live
                Game View opens. */}
            <PlayerHistoryPanel open={!!openPlayer} onOpenChange={(v) => !v && setOpenPlayer(null)}
                                organizationId={data?.organization_id} player={openPlayer}
                                managerToken={managerToken} t={t} prefix="game-results" />

            {/* Tap-to-open details — the same panel idea as Review Shield Results. */}
            <Sheet open={!!openRow} onOpenChange={(v) => !v && setOpenRow(null)}>
                <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="game-results-detail">
                    {openRow && (
                        <div className="space-y-3 pt-6">
                            <p className="text-lg font-bold">{t('gameResults.detailTitle', 'This play')}</p>
                            {COLUMN_ORDER.filter((c) => (caps.columns || []).includes(c)).map((c) => (
                                <div key={c} className="flex justify-between gap-3 border-b pb-1.5" data-testid={`game-results-detail-${c}`}>
                                    <span className="text-xs text-muted-foreground">{header(c)}</span>
                                    <span className="text-sm text-right">{cell(openRow, c)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            {/* Customize — super admin only. Hiding is a view setting, never a delete. */}
            <Sheet open={customize} onOpenChange={setCustomize}>
                <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="game-results-customize">
                    <div className="space-y-4 pt-6">
                        <div className="flex items-center justify-between">
                            <p className="text-lg font-bold">{t('gameResults.customize', 'Customize')}</p>
                            <Button variant="outline" size="sm" onClick={resetHidden} data-testid="game-results-reset-btn">
                                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                                {t('gameResults.resetDefault', 'Reset to default')}
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('gameResults.customizeHelp', 'Turning something off only hides it on this page. No data is ever deleted.')}
                        </p>
                        <p className="text-sm font-semibold">{t('gameResults.columnsLabel', 'Columns')}</p>
                        {(caps.columns || []).map((c) => (
                            <div key={c} className="flex items-center justify-between py-1">
                                <span className="text-sm">{header(c)}</span>
                                <Switch checked={!hiddenColumns.includes(c)}
                                        onCheckedChange={(on) => saveHidden('column', c, on)}
                                        data-testid={`game-results-toggle-column-${c}`} />
                            </div>
                        ))}
                        <p className="text-sm font-semibold pt-2">{t('gameResults.filtersLabel', 'Filters')}</p>
                        {(caps.filters || []).map((f) => (
                            <div key={f} className="flex items-center justify-between py-1">
                                <span className="text-sm">{t(`gameResults.filterName.${f}`, f)}</span>
                                <Switch checked={!hiddenFilters.includes(f)}
                                        onCheckedChange={(on) => saveHidden('filter', f, on)}
                                        data-testid={`game-results-toggle-filter-${f}`} />
                            </div>
                        ))}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}

export default function GameResults(props) {
    const { hasPermission } = useAuth();
    // A signed NEO manager session reaches this page through its own token and
    // keeps the access it has always had.
    if (!props.managerToken && !hasPermission('view_game_results')) return <NoGoalAccess />;
    return <GameResultsPage {...props} />;
}
